import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import alertService from '../../../services/alertService';

const AlertDashboardWidget = () => {
  const [stats, setStats] = useState(null);
  const [recentAlerts, setRecentAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
    
    // Subscribe to real-time alerts
    const channel = alertService?.subscribeToAlerts(() => {
      loadData();
    });
    
    return () => {
      alertService?.unsubscribeFromAlerts(channel);
    };
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [statsData, alertsData] = await Promise.all([
        alertService?.getAlertStats(),
        alertService?.getUnreadAlerts()
      ]);
      setStats(statsData);
      setRecentAlerts(alertsData?.slice(0, 5) || []);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical':
        return { name: 'AlertTriangle', color: 'var(--color-error)' };
      case 'high':
        return { name: 'AlertCircle', color: 'var(--color-warning)' };
      case 'medium':
        return { name: 'Info', color: 'var(--color-accent)' };
      default:
        return { name: 'Info', color: 'var(--color-muted-foreground)' };
    }
  };

  const getAlertTypeLabel = (type) => {
    switch (type) {
      case 'nasfund_deadline':
        return 'NASFUND Deadline';
      case 'swt_filing_error':
        return 'SWT Filing Error';
      case 'compliance_violation':
        return 'Compliance Violation';
      case 'payroll_approval_status':
        return 'Payroll Approval';
      default:
        return type;
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return date?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6 w-full">
        <div className="flex items-center justify-center py-8">
          <Icon name="Loader" size={32} color="var(--color-muted-foreground)" className="animate-spin" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card border border-border rounded-lg p-6 w-full">
        <div className="text-center py-8 text-muted-foreground">
          <Icon name="Clock" size={32} color="var(--color-muted-foreground)" className="mx-auto mb-2" />
          <p className="text-sm break-words">Thank you for visiting. Pacific RBS Ltd is introducing full HR Management System for Private and Public Sector for 2026. This website is launching soon!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6">
      <div className="flex items-center gap-2 mb-4">
        <Icon name="Bell" size={20} color="var(--color-primary)" />
        <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground">
          Alert Summary
        </h3>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-muted/50 rounded-md p-3">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="Bell" size={14} color="var(--color-muted-foreground)" />
            <p className="text-xs caption text-muted-foreground">Total</p>
          </div>
          <p className="text-xl md:text-2xl font-bold data-text text-foreground">
            {stats?.total || 0}
          </p>
        </div>
        <div className="bg-error/10 rounded-md p-3">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="AlertTriangle" size={14} color="var(--color-error)" />
            <p className="text-xs caption text-error">Critical</p>
          </div>
          <p className="text-xl md:text-2xl font-bold data-text text-error">
            {stats?.critical || 0}
          </p>
        </div>
        <div className="bg-accent/10 rounded-md p-3">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="BellRing" size={14} color="var(--color-accent)" />
            <p className="text-xs caption text-accent">Unread</p>
          </div>
          <p className="text-xl md:text-2xl font-bold data-text text-accent">
            {stats?.unread || 0}
          </p>
        </div>
        <div className="bg-success/10 rounded-md p-3">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="CheckCircle" size={14} color="var(--color-success)" />
            <p className="text-xs caption text-success">Read</p>
          </div>
          <p className="text-xl md:text-2xl font-bold data-text text-success">
            {(stats?.total || 0) - (stats?.unread || 0)}
          </p>
        </div>
      </div>

      {/* Recent Alerts */}
      <div>
        <h4 className="font-body font-semibold text-sm text-foreground mb-3">
          Recent Alerts
        </h4>
        <div className="space-y-2">
          {recentAlerts?.length === 0 ? (
            <div className="text-center py-6">
              <Icon name="CheckCircle" size={32} color="var(--color-success)" className="mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No unread alerts</p>
            </div>
          ) : (
            recentAlerts?.map((alert) => {
              const iconConfig = getSeverityIcon(alert?.severity);
              return (
                <div
                  key={alert?.id}
                  className="flex items-start gap-3 p-3 bg-muted/30 rounded-md hover:bg-muted/50 transition-smooth"
                >
                  <Icon name={iconConfig?.name} size={16} color={iconConfig?.color} className="mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <p className="text-xs font-medium text-foreground line-clamp-1">
                        {alert?.title}
                      </p>
                      <span className="text-xs caption text-muted-foreground whitespace-nowrap">
                        {formatTimestamp(alert?.createdAt)}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-1 mb-1">
                      {alert?.message}
                    </p>
                    <span className="text-xs caption text-accent">
                      {getAlertTypeLabel(alert?.alertType)}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default AlertDashboardWidget;